import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
  Button,
  Divider,
  FormControlLabel,
  Menu,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import Datepicker from 'react-datepicker';
import { endOfDay, format, startOfDay, subDays } from 'date-fns';

import './index.scss';

const PRESET_IDS = {
  today: 'today',
  previous7: 'previous7',
  previous14: 'previous14',
  previous30: 'previous30',
  previous365: 'previous365',
  custom: 'custom',
};

const buildPresetOptions = (messages) => [
  {
    id: PRESET_IDS.today,
    label: messages['evirPx_Date_Range_Today'],
    getRange: () => {
      const endDate = endOfDay(new Date());
      return {
        startDate: startOfDay(endDate),
        endDate,
      };
    },
  },
  {
    id: PRESET_IDS.previous7,
    label: messages['evirPx_Date_Range_Previous_7'],
    getRange: () => {
      const endDate = endOfDay(new Date());
      return {
        startDate: startOfDay(subDays(endDate, 7)),
        endDate,
      };
    },
  },
  {
    id: PRESET_IDS.previous14,
    label: messages['evirPx_Date_Range_Previous_14'],
    getRange: () => {
      const endDate = endOfDay(new Date());
      return {
        startDate: startOfDay(subDays(endDate, 14)),
        endDate,
      };
    },
  },
  {
    id: PRESET_IDS.previous30,
    label: messages['evirPx_Date_Range_Previous_30'],
    getRange: () => {
      const endDate = endOfDay(new Date());
      return {
        startDate: startOfDay(subDays(endDate, 30)),
        endDate,
      };
    },
  },
  {
    id: PRESET_IDS.previous365,
    label: messages['evirPx_Date_Range_Previous_365'],
    getRange: () => {
      const endDate = endOfDay(new Date());
      return {
        startDate: startOfDay(subDays(endDate, 365)),
        endDate,
      };
    },
  },
  {
    id: PRESET_IDS.custom,
    label: messages['evirPx_Date_Range_Custom'],
    isCustom: true,
  },
];

const detectPreset = (range, presetOptions) => {
  if (!range?.startDate || !range?.endDate) {
    return PRESET_IDS.custom;
  }

  const normalizedStart = startOfDay(range.startDate);
  const normalizedEnd = endOfDay(range.endDate);

  const matchedPreset = presetOptions.find((preset) => {
    if (preset.isCustom) {
      return false;
    }

    const presetRange = preset.getRange();
    return presetRange.startDate.getTime() === normalizedStart.getTime()
      && presetRange.endDate.getTime() === normalizedEnd.getTime();
  });

  return matchedPreset?.id || PRESET_IDS.custom;
};

const formatRangeLabel = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return '';
  }

  return `${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`;
};

const DateRangeFilter = ({
  id = undefined,
  label = '',
  value = null,
  onApply = undefined,
}) => {
  const { messages } = useIntl();
  const presets = useMemo(() => buildPresetOptions(messages), [messages]);
  const fallbackRange = useMemo(() => {
    const preset = presets.find((item) => item.id === PRESET_IDS.previous7);
    if (preset) {
      return preset.getRange();
    }

    const endDate = endOfDay(new Date());
    return {
      startDate: startOfDay(subDays(endDate, 7)),
      endDate,
    };
  }, [presets]);

  const initialRange = value?.startDate && value?.endDate ? value : fallbackRange;
  const [currentRange, setCurrentRange] = useState(initialRange);
  const [currentPreset, setCurrentPreset] = useState(() => detectPreset(initialRange, presets));
  const [pendingRange, setPendingRange] = useState(initialRange);
  const [pendingPreset, setPendingPreset] = useState(() => detectPreset(initialRange, presets));
  const [anchorEl, setAnchorEl] = useState(null);
  const isOpen = Boolean(anchorEl);
  const triggerId = id || 'date-range-filter-trigger';
  const controlledStartTime = value?.startDate ? value.startDate.getTime() : null;
  const controlledEndTime = value?.endDate ? value.endDate.getTime() : null;
  const fallbackStartTime = fallbackRange?.startDate ? fallbackRange.startDate.getTime() : null;
  const fallbackEndTime = fallbackRange?.endDate ? fallbackRange.endDate.getTime() : null;

  useEffect(() => {
    if (value?.startDate && value?.endDate) {
      const nextRange = {
        startDate: value.startDate,
        endDate: value.endDate,
      };
      const presetId = detectPreset(nextRange, presets);
      setCurrentRange(nextRange);
      setCurrentPreset(presetId);
      setPendingRange(nextRange);
      setPendingPreset(presetId);
      return;
    }

    if (fallbackRange?.startDate && fallbackRange?.endDate) {
      const presetId = detectPreset(fallbackRange, presets);
      setCurrentRange(fallbackRange);
      setCurrentPreset(presetId);
      setPendingRange(fallbackRange);
      setPendingPreset(presetId);
    }
  }, [controlledStartTime, controlledEndTime, fallbackStartTime, fallbackEndTime, presets, fallbackRange]);

  const openMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setPendingRange(currentRange);
    setPendingPreset(currentPreset);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const handlePresetChange = (event) => {
    const presetId = event.target.value;
    setPendingPreset(presetId);

    if (presetId === PRESET_IDS.custom) {
      return;
    }

    const preset = presets.find((option) => option.id === presetId);
    if (preset) {
      setPendingRange(preset.getRange());
    }
  };

  const handleCalendarChange = (dates) => {
    const [start, end] = dates;

    setPendingRange({
      startDate: start ? startOfDay(start) : null,
      endDate: end ? endOfDay(end) : null,
    });

    if (start && end) {
      setPendingPreset(PRESET_IDS.custom);
    }
  };

  const handleCancel = () => {
    closeMenu();
  };

  const handleApply = () => {
    if (!pendingRange.startDate || !pendingRange.endDate) {
      return;
    }

    setCurrentRange(pendingRange);
    setCurrentPreset(pendingPreset);
    onApply?.({
      startDate: pendingRange.startDate,
      endDate: pendingRange.endDate,
      preset: pendingPreset,
    });
    closeMenu();
  };

  const triggerLabel = currentPreset === PRESET_IDS.custom
    ? formatRangeLabel(currentRange?.startDate, currentRange?.endDate)
    : presets.find((preset) => preset.id === currentPreset)?.label;

  const triggerText = triggerLabel || label || '';

  const canApply = Boolean(pendingRange.startDate && pendingRange.endDate);

  return (
    <div className='date-range-filter'>
      <Typography
        variant='caption'
        className='date-range-filter-label'
      >
        {label}
      </Typography>
      <button
        id={triggerId}
        type='button'
        className='date-range-filter-trigger'
        onClick={openMenu}
        aria-haspopup='true'
        aria-expanded={isOpen ? 'true' : undefined}
      >
        <span>{triggerText}</span>
        {isOpen ? (
          <KeyboardArrowUp className='date-range-filter-trigger-icon' />
        ) : (
          <KeyboardArrowDown className='date-range-filter-trigger-icon' />
        )}
      </button>
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleCancel}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        MenuListProps={{ 'aria-labelledby': triggerId }}
        PaperProps={{ className: 'date-range-filter-menu-paper' }}
      >
        <div className='date-range-filter-menu' onClick={(event) => event.stopPropagation()}>
          <div className='date-range-filter-presets'>
            <RadioGroup
              value={pendingPreset}
              onChange={handlePresetChange}
              name={`${triggerId}-presets`}
            >
              {presets.map((preset) => (
                <FormControlLabel
                  key={preset.id}
                  value={preset.id}
                  control={<Radio />}
                  className='date-range-filter-preset'
                  label={preset.label}
                />
              ))}
            </RadioGroup>
          </div>
          <Divider orientation='vertical' flexItem />
          <div className='date-range-filter-calendar-section'>
            <Typography variant='subtitle2' className='date-range-filter-calendar-label'>
              {messages['evirPx_Date_Range']}
            </Typography>
            <Datepicker
              inline
              selectsRange
              selected={pendingRange.startDate}
              startDate={pendingRange.startDate}
              endDate={pendingRange.endDate}
              onChange={handleCalendarChange}
              monthsShown={1}
              calendarClassName='date-range-filter-calendar'
              shouldCloseOnSelect={false}
              maxDate={new Date()}
            />
            <Stack direction='row' spacing={1.5} className='date-range-filter-actions'>
              <Button variant='outlined' fullWidth onClick={handleCancel}>
                {messages['evirPx_Cancel']}
              </Button>
              <Button variant='contained' fullWidth onClick={handleApply} disabled={!canApply}>
                {messages['evirPx_Apply']}
              </Button>
            </Stack>
          </div>
        </div>
      </Menu>
    </div>
  );
};

DateRangeFilter.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.shape({
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
    preset: PropTypes.string,
  }),
  onApply: PropTypes.func,
};

export default DateRangeFilter;
